import React, { useEffect, useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PrintIcon from "@mui/icons-material/Print";
import baseUrl from "../../../../api/baseUrl";
import styles from "./TablePopup.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  addTableOrderItem,
  increaseTableOrderItemQty,
  decreaseTableOrderItemQty,
  removeTableOrderItem,
  updateTableOrderDiscount,
  setTableOrderStatus,
  setTable,
  clearTableOrder,
  addKotPrintTime,
  saveCurrentTableOrder,
  setPaidAmount,
} from "../../../../store/tableOrder/tableOrderSlice";
import { RootState } from "../../../../store/store";
import ReceiptNew from "../../../../views/print/ReceiptNew";
import "./../../../../views/print/receipt.scss";
import { getDecryptedToken } from "../../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../../utils/currency/currencyUtils";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import useSubmitting from "../../../../hooks/useSubmitting";

interface TablePopupProps {
  tableNumber: string;
  peopleCount: number;
  tableId?: number;
  onClose: () => void;
}

const statusOptions = ["Available", "Ordered", "Ongoing", "Reserved"];

const TablePopup: React.FC<TablePopupProps> = ({
  tableNumber,
  peopleCount,
  tableId,
  onClose,
}) => {
  const dispatch = useDispatch();
  const tableOrder = useSelector(
    (state: RootState) => state.tableOrder.current,
  );
  const navigate = useNavigate();

  // Fetch items for add-item dropdown
  const [items, setItems] = useState<any[]>([]);
  const [addForm, setAddForm] = useState({ itemId: "", qty: 1, discount: 0 });
  const [showPayment, setShowPayment] = useState(false);
  const paid = tableOrder.paid || 0;
  const [showPrintPopup, setShowPrintPopup] = useState(false);
  const [showFinalPrintPopup, setShowFinalPrintPopup] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const finalPrintRef = useRef<HTMLDivElement>(null);

  // KOT-style order details from API
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tableStatus, setTableStatus] = useState<string>("Available");
  const [statusLoading, setStatusLoading] = useState(false);
  const [billOrderNumber, setBillOrderNumber] = useState<string>("");

  useEffect(() => {
    baseUrl.get("/branch/pos/find-items").then((res) => {
      setItems(res.data.data || []);
    });
    // Set table info in redux
    dispatch(setTable({ tableNumber, peopleCount, tableId }));
    // eslint-disable-next-line
  }, [tableNumber, peopleCount, tableId]);

  // Fetch order details and table status for this table from API (like KOT)
  useEffect(() => {
    if (!tableId) {
      setOrderDetails([]);
      setTableStatus("Available");
      setBillOrderNumber("");
      return;
    }
    setLoading(true);
    baseUrl
      .get(`/branch/order/table/${tableId}`)
      .then((res) => {
        const orders = res.data.data || [];
        setOrderDetails(orders);
        setBillOrderNumber(orders[0]?.orderNumber || "");
      })
      .catch((err) => {
        console.error("Error fetching order details:", err);
        setOrderDetails([]);
        setBillOrderNumber("");
      })
      .finally(() => setLoading(false));
    // Fetch table status from API
    baseUrl
      .get(`/branch/table/get-table/${tableId}`)
      .then((res) => setTableStatus(res.data.data?.status || "Available"))
      .catch(() => setTableStatus("Available"));
  }, [tableId]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.itemId || !billOrderNumber) return;
    const item = items.find((i) => i.id === Number(addForm.itemId));
    if (!item) return;
    try {
      await baseUrl.post("/branch/order/create-order", {
        orders: [
          {
            itemId: item.id,
            quantity: addForm.qty,
            orderType: "table",
            status: "Started",
          },
        ],
        tableId,
        peopleCount,
        waiterId: 1,
        orderNumber: billOrderNumber,
      });
      // Refresh order details
      baseUrl
        .get(`/branch/order/table/${tableId}`)
        .then((res) => setOrderDetails(res.data.data || []));
      setAddForm({ itemId: "", qty: 1, discount: 0 });
    } catch (err) {
      console.error("Error adding item:", err);
    }
  };
  const [submitting, wrap] = useSubmitting();

  // Delete item from order if status is "Started"
  const handleDeleteOrderItem = async (orderId: number) => {
    try {
      await baseUrl.put(`/branch/order/item/${orderId}/status`, {
        status: "Cancelled",
      });
      setOrderDetails((prev) =>
        prev.map((o: any) =>
          o.id === orderId ? { ...o, status: "Cancelled" } : o,
        ),
      );
    } catch (err) {
      console.error("Error deleting order item:", err);
    }
  };

  // Calculate totals for table bill (use orderDetails, not tableOrder.orders)
  const tableBillTotal = orderDetails.reduce(
    (sum: number, order: any) =>
      sum + Number(order.price || 0) * Number(order.quantity || 0),
    0,
  );

  // Print normal bill (show print popup, then print)
  const handlePrint = () => {
    setShowPrintPopup(true);
  };

  // Print final bill (show final print popup, then print)
  const handleFinishBill = async () => {
    if (!billOrderNumber) return;
    setShowPayment(true);
    // Optionally, you can update all items to "Completed" here after payment
    // Or do it in handleFinalPrintConfirm after payment is done
  };

  // Actually print when user confirms in popup
  const handlePrintConfirm = async () => {
    if (printRef.current) {
      try {
        printRef.current.style.display = "block";
        // Use window.print() on a hidden iframe for direct print, no new tab
        const printContents = printRef.current.innerHTML;
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.right = "0";
        printFrame.style.bottom = "0";
        printFrame.style.width = "0";
        printFrame.style.height = "0";
        printFrame.style.border = "0";
        document.body.appendChild(printFrame);

        printFrame.contentDocument!.open();
        printFrame.contentDocument!.write(`
          <html>
            <head>
              <title>Print Bill</title>
              <link rel="stylesheet" href="/src/views/print/receipt.scss" />
              <style>
                body { background: #fff; margin: 0; }
              </style>
            </head>
            <body>${printContents}</body>
          </html>
        `);
        printFrame.contentDocument!.close();

        printFrame.onload = () => {
          printFrame.contentWindow!.focus();
          printFrame.contentWindow!.print();
          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 1000);
        };
      } catch (err) {}
    }
    setShowPrintPopup(false);
    dispatch(addKotPrintTime());
    dispatch(saveCurrentTableOrder());
  };

  //   const handleFinalPrintConfirm = async () => {
  //     if (finalPrintRef.current) {
  //       try {
  //         finalPrintRef.current.style.display = "block";
  //         const element = finalPrintRef.current;
  //         const html2canvas = (await import("html2canvas")).default;
  //         const jsPDF = (await import("jspdf")).jsPDF;
  //         const options = {
  //           scale: 2,
  //           useCORS: true,
  //           allowTaint: true,
  //         };
  //         const canvas = await html2canvas(element, options);
  //         const imgData = canvas.toDataURL("image/png");
  //         const imgWidth = 80;
  //         const imgProps = canvas.width / canvas.height;
  //         const imgHeight = imgWidth / imgProps;
  //         const pdf = new jsPDF({
  //           orientation: "p",
  //           unit: "mm",
  //           format: [imgWidth, imgHeight],
  //         });
  //         pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST");
  //         pdf.autoPrint && pdf.autoPrint();
  //         const pdfBlob = pdf.output("blob");
  //         const blobUrl = URL.createObjectURL(pdfBlob);

  //         const printWindow = window.open(blobUrl, "_blank");
  //         if (printWindow) {
  //           printWindow.onload = () => {
  //             printWindow.focus();
  //             printWindow.print();
  //           };
  //         }
  //       } catch (err) {}
  //     }
  //     setShowFinalPrintPopup(false);
  //     dispatch(addKotPrintTime());
  //     dispatch(saveCurrentTableOrder());
  //     dispatch(clearTableOrder());
  //     onClose();
  //   };

  const handlePay = () => {
    setShowPayment(false);
    // dispatch(setPaidAmount(0));
    setShowFinalPrintPopup(true);
  };

  // Add shop/company details for receipt header
  const shopName = getDecryptedToken("martName");
  const address = getDecryptedToken("address");
  const phoneNumber = getDecryptedToken("phoneNumber");
  const name = getDecryptedToken("name");
  const email = getDecryptedToken("email");
  const currency = getDecryptedToken("currency");
  const currencySymbol = getCurrencySymbol();

  // Add state for loading and error
  const [isPrinting, setIsPrinting] = useState(false);

  // Helper: Prepare sale payload for API
  const getSalePayload = () => {
    const drawerId = getDecryptedToken("selectedDrawer");
    return {
      productData: tableOrder.orders.map((item: any) => ({
        itemId: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        discount: item.discount,
        priceHistoryId: item.priceHistoryId,
        ...item,
      })),
      card: 0,
      cash: paid,
      bank: 0,
      balance: paid - tableBillTotal,
      discount: 0,
      givenDiscount: 0,
      tax: 0,
      total: tableBillTotal,
      netTotal: tableBillTotal,
      drawerId: Number(drawerId),
      customerId: null,
      saleType: "cash",
      creditSale: false,
      // You may add tableId or tableNumber if needed for backend
      tableId: tableOrder.tableId,
      tableNumber: tableOrder.tableNumber,
    };
  };

  // Print and complete sale (API call, print, clear, close)
  const handleFinalPrintConfirm = async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    const loadingToastId = toast.loading("Processing sale...", {
      position: "top-center",
    });
    try {
      // 1. Call API (same as POS)
      const payload = getSalePayload();
      const res = await baseUrl.post("/branch/pos/create-sale", payload);

      // 2. Print receipt (same as POS)
      if (finalPrintRef.current) {
        finalPrintRef.current.style.display = "block";
        const element = finalPrintRef.current;
        const options = {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        };
        const canvas = await html2canvas(element, options);
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = 80;
        const imgProps = canvas.width / canvas.height;
        const imgHeight = imgWidth / imgProps;
        const pdf = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: [imgWidth, imgHeight],
        });
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST");
        pdf.autoPrint && pdf.autoPrint();
        const pdfBlob = pdf.output("blob");
        const blobUrl = URL.createObjectURL(pdfBlob);

        const printWindow = window.open(blobUrl, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
          };
        }
      }

      // 3. Mark all items as Completed (close bill)
      if (billOrderNumber) {
        await baseUrl.put(
          `/branch/order/order-number/${billOrderNumber}/status`,
          { status: "Completed" },
        );
      }

      // 4. Success toast
      toast.dismiss(loadingToastId);
      toast.success("Table bill completed successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      // 5. Clear order, close popup
      setShowFinalPrintPopup(false);
      dispatch(addKotPrintTime());
      dispatch(saveCurrentTableOrder());
      dispatch(clearTableOrder());
      onClose();
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      // Error handling (same as POS)
      const errorMessage = error.response?.data?.message;
      if (errorMessage) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        toast.error("Failed to process sale. Please try again.", {
          position: "top-center",
          autoClose: 5000,
        });
      }
      // Do not clear/close/print
    } finally {
      setIsPrinting(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!billOrderNumber) return;

    const loadingToastId = toast.loading("Cancelling order...", {
      position: "top-center",
    });

    try {
      await baseUrl.put(`/branch/order/cancel-order/${billOrderNumber}`);

      toast.dismiss(loadingToastId);
      toast.success("Order cancelled successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      setOrderDetails([]);
      setBillOrderNumber("");
      setTableStatus("Available");
      onClose();
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      const errorMessage = error.response?.data?.message;
      if (errorMessage) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        toast.error("Failed to cancel order. Please try again.", {
          position: "top-center",
          autoClose: 5000,
        });
      }
    }
  };

  // Send KOT handler: save all table orders to redux, then navigate to KOT
  const handleSendKot = () => {
    // Save current table order to redux (if not already)
    dispatch(saveCurrentTableOrder());
    // Navigate to KOT page (no state needed, KOT reads from redux)
    navigate("/kot");
  };

  // (Removed duplicate state declarations and useEffect for order details and table status)

  // Update table status via API
  const handleStatusChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newStatus = e.target.value;
    setStatusLoading(true);
    try {
      await baseUrl.patch(`/branch/table/update-table-status/${tableId}`, {
        status: newStatus,
      });
      setTableStatus(newStatus);
      dispatch(setTableOrderStatus(newStatus));
    } catch {
      // fallback: revert to previous status
      dispatch(setTableOrderStatus(tableStatus));
    }
    setStatusLoading(false);
  };

  return (
    <div className={styles["table-popup-modal"]}>
      <div className={styles["table-popup-content"]}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div className={styles["popup-header"]} style={{ flex: 1 }}>
            <h3
              style={{
                margin: 0,
                textAlign: "left",
                fontWeight: 700,
              }}
            >
              Table {tableOrder.tableNumber}
              <span
                className={styles["people"]}
                style={{
                  fontWeight: 400,
                  fontSize: "1rem",
                  color: "#888",
                  marginLeft: 8,
                }}
              >
                ({tableOrder.peopleCount} People)
              </span>
            </h3>
          </div>
          <button
            onClick={onClose}
            className={styles["close-btn"]}
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              marginLeft: 12,
              cursor: "pointer",
              color: "#222",
              padding: 4,
              borderRadius: "50%",
              transition: "background 0.2s",
            }}
            aria-label="Close"
            title="Close"
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles["status-select"]} style={{ marginBottom: 12 }}>
          <label>
            Status:{" "}
            <select
              value={tableStatus}
              onChange={handleStatusChange}
              disabled={statusLoading}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {statusLoading && (
              <span style={{ marginLeft: 8, color: "#888" }}>Updating...</span>
            )}
          </label>
        </div>
        {/* KOT-style order details */}
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "8px 0" }}>Current Orders</h4>
          {loading ? (
            <div style={{ color: "#888" }}>Loading...</div>
          ) : orderDetails.length === 0 ? (
            <div style={{ color: "#888" }}>No order details found.</div>
          ) : (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Order #</th>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.map((order: any, idx: number) => (
                    <tr key={order.id || idx}>
                      <td>{order.orderNumber}</td>
                      <td>
                        {order.itemName || order.name}
                        <br />
                        <span style={{ fontSize: 11, color: "#888" }}>
                          {order.itemNumber}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>{order.quantity}</td>
                      <td style={{ textAlign: "center" }}>
                        {order.price ? Number(order.price).toFixed(2) : "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {order.price && order.quantity
                          ? (
                              Number(order.price) * Number(order.quantity)
                            ).toFixed(2)
                          : "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span
                          style={{
                            background:
                              order.status === "Started"
                                ? "#F2A91C"
                                : order.status === "Preparing"
                                  ? "#F2A91C"
                                  : order.status === "Ready"
                                    ? "#067904"
                                    : order.status === "Served"
                                      ? "#3FC091"
                                      : order.status === "Completed"
                                        ? "#4763e4"
                                        : order.status === "Cancelled"
                                          ? "#DC1818"
                                          : "#D7D7D7",
                            color: "#fff",
                            padding: "2px 10px",
                            borderRadius: 6,
                            fontWeight: 600,
                          }}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {order.status === "Started" && (
                          <button
                            style={{
                              background: "#DC1818",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              padding: "4px 10px",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                            onClick={() => handleDeleteOrderItem(order.id)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Show total for all items in this bill */}
                <tfoot>
                  <tr>
                    <td
                      colSpan={4}
                      style={{ textAlign: "right", fontWeight: 600 }}
                    >
                      Total:
                    </td>
                    <td style={{ textAlign: "center", fontWeight: 600 }}>
                      {orderDetails
                        .reduce(
                          (sum: number, order: any) =>
                            sum +
                            Number(order.price || 0) *
                              Number(order.quantity || 0),
                          0,
                        )
                        .toFixed(2)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </>
          )}
        </div>
        {/* Add Item Section (only if bill is open) */}
        {billOrderNumber && (
          <form
            className={styles["add-item-form"]}
            onSubmit={wrap(handleAddItem)}
            style={{ marginTop: 16 }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select
                name="itemId"
                value={addForm.itemId}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, itemId: e.target.value }))
                }
                required
                style={{ flex: 2 }}
              >
                <option value="">Select Item</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} {item.itemNumber ? `(${item.itemNumber})` : ""}
                  </option>
                ))}
              </select>
              <input
                type="number"
                name="qty"
                value={addForm.qty}
                min={1}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, qty: Number(e.target.value) }))
                }
                style={{ width: 60 }}
                required
              />
              <button
                type="submit"
                style={{ padding: "6px 12px" }}
                disabled={submitting}
              >
                {submitting ? "Adding..." : "Add"}
              </button>
            </div>
          </form>
        )}
        {/* Actions */}
        <div className={styles["popup-actions"]}>
          <button className={styles["send-kot-btn"]} onClick={handleSendKot}>
            Send to KOT
          </button>
          <button className={styles["print-bill-btn"]} onClick={handlePrint}>
            Print Bill
          </button>
          <button
            className={styles["finish-bill-btn"]}
            onClick={handleFinishBill}
          >
            Finish Bill
          </button>
          <button
            className={styles["cancel-order-btn"]}
            onClick={handleCancelOrder}
            style={{ backgroundColor: "#DC1818" }}
          >
            Cancel Order
          </button>
        </div>
        {/* Totals */}
        <div className={styles["totals-section"]}>
          <div className={styles["total-row"]}>
            <span className={styles["label"]}>Total:</span>
            <span className={styles["value"]}>{tableBillTotal.toFixed(2)}</span>
          </div>
          {showPayment && (
            <div className={styles["payment-section"]}>
              <label>
                Paid:{" "}
                <input
                  type="number"
                  value={paid}
                  min={0}
                  onChange={(e) =>
                    dispatch(setPaidAmount(Number(e.target.value)))
                  }
                  style={{ width: 80 }}
                />
              </label>
              <div>Balance: {(paid - tableBillTotal).toFixed(2)}</div>
              <button
                className={styles["pay-btn"]}
                onClick={handlePay}
                type="button"
              >
                Pay & Finish
              </button>
            </div>
          )}
        </div>
        {/* Print Bill Popup (Receipt style) */}
        {showPrintPopup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3000,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: 24,
                minWidth: 350,
                boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "auto",
                maxHeight: "90vh",
                maxWidth: "95vw",
              }}
            >
              {/* Receipt-style print for table bill */}
              <div ref={printRef}>
                <div className="receipt" style={{ background: "white" }}>
                  <div className="headers">
                    <div className="title">{shopName}</div>
                    <div className="address">{address}</div>
                    <div className="tell">{phoneNumber}</div>
                    <div className="email">{email}</div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: 700,
                      fontSize: 18,
                      marginBottom: 8,
                    }}
                  >
                    TABLE BILL
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>TABLE:</strong> {tableOrder.tableNumber} &nbsp;
                    <strong>PEOPLE:</strong> {tableOrder.peopleCount}
                  </div>
                  <hr />
                  <div className="heading">
                    <div className="row1">
                      <span>LN</span>
                      <span>ITEMS</span>
                    </div>
                    <div className="row2">
                      <span>QTY</span>
                      <span>RATE</span>
                      <span>GROSS</span>
                      <span>DIS.</span>
                      <span>NET AMT</span>
                    </div>
                  </div>
                  <hr />
                  {orderDetails.map((item: any, i: number) => (
                    <div className="r_items" key={i}>
                      <div className="row1">
                        <span>{String(i + 1).padStart(2, "0")}.</span>
                        <span>{item?.itemName || item?.name}</span>
                      </div>
                      <div className="row2">
                        <span>{item?.quantity}</span>
                        <span>
                          {currencySymbol} {Number(item?.price).toFixed(2)}
                        </span>
                        <span>X</span>
                        <span>0</span>
                        <span>
                          {currencySymbol}{" "}
                          {(
                            Number(item?.price) * Number(item?.quantity)
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                  <hr />
                  <div
                    style={{
                      textAlign: "right",
                      fontWeight: 600,
                      marginTop: 8,
                    }}
                  >
                    TOTAL: {currencySymbol}
                    {tableBillTotal.toFixed(2)}
                  </div>
                  <div className="thank" style={{ marginTop: 12 }}>
                    THANK YOU! COME AGAIN
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 18, display: "flex", gap: 16 }}>
                <button
                  style={{
                    background: "#4763e4",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 24px",
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                  onClick={handlePrintConfirm}
                >
                  Print
                </button>
                <button
                  style={{
                    background: "#ccc",
                    color: "#333",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 24px",
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                  onClick={() => setShowPrintPopup(false)}
                >
                  Cancel
                </button>
              </div>
              {/* Add button to open final bill popup */}
              <div style={{ marginTop: 12, width: "100%" }}>
                <button
                  style={{
                    background: "#218739",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 24px",
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: "pointer",
                    width: "100%",
                  }}
                  onClick={() => setShowFinalPrintPopup(true)}
                >
                  Print Final Bill
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Print Final Bill Popup (Receipt style) */}
        {showFinalPrintPopup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3000,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: 24,
                minWidth: 350,
                boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "auto",
                maxHeight: "90vh",
                maxWidth: "95vw",
              }}
            >
              <div ref={finalPrintRef}>
                <div className="receipt" style={{ background: "white" }}>
                  <div className="headers">
                    <div className="title">{shopName}</div>
                    <div className="address">{address}</div>
                    <div className="tell">{phoneNumber}</div>
                    <div className="email">{email}</div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      fontWeight: 700,
                      fontSize: 18,
                      marginBottom: 8,
                    }}
                  >
                    FINAL TABLE BILL
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>TABLE:</strong> {tableOrder.tableNumber} &nbsp;
                    <strong>PEOPLE:</strong> {tableOrder.peopleCount}
                  </div>
                  <hr />
                  <div className="heading">
                    <div className="row1">
                      <span>LN</span>
                      <span>ITEMS</span>
                    </div>
                    <div className="row2">
                      <span>QTY</span>
                      <span>RATE</span>
                      <span>GROSS</span>
                      <span>DIS.</span>
                      <span>NET AMT</span>
                    </div>
                  </div>
                  <hr />
                  {/* Use orderDetails for final bill */}
                  {orderDetails?.map((item: any, i: number) => (
                    <div className="r_items" key={i}>
                      <div className="row1">
                        <span>{String(i + 1).padStart(2, "0")}.</span>
                        <span>{item?.itemName || item?.name}</span>
                      </div>
                      <div className="row2">
                        <span>{item?.quantity}</span>
                        <span>
                          {currencySymbol} {Number(item?.price).toFixed(2)}
                        </span>
                        <span>
                          {currencySymbol}{" "}
                          {(
                            Number(item?.price) * Number(item?.quantity)
                          ).toFixed(2)}
                        </span>
                        <span>{item?.discount || 0}</span>
                        <span>
                          {currencySymbol}{" "}
                          {(
                            Number(item?.price) * Number(item?.quantity) -
                            parseFloat(item?.discount || 0) *
                              Number(item?.quantity)
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                  <hr />
                  {/* Payment details section */}
                  <div className="amounts" style={{ marginTop: 8 }}>
                    <div className="datas">
                      <span>Total</span>
                      <span>
                        {currencySymbol}
                        {orderDetails
                          .reduce(
                            (sum: number, item: any) =>
                              sum +
                              (Number(item.price) * Number(item.quantity) -
                                parseFloat(item.discount || 0) *
                                  Number(item.quantity)),
                            0,
                          )
                          .toFixed(2)}
                      </span>
                    </div>
                    {/* Final Discount */}
                    <div className="datas">
                      <span>Final Discount</span>
                      <span>
                        {currencySymbol}{" "}
                        {(
                          orderDetails.reduce(
                            (sum: number, item: any) =>
                              sum +
                              parseFloat(item.discount || 0) *
                                Number(item.quantity),
                            0,
                          ) || 0
                        ).toFixed(2)}
                      </span>
                    </div>
                    {/* Payment breakdown */}
                    <div className="datas">
                      <span>Cash</span>
                      <span>
                        {currencySymbol}{" "}
                        {(tableOrder.cash || tableOrder.paid || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="datas">
                      <span>Card</span>
                      <span>
                        {currencySymbol} {(tableOrder.card || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="datas">
                      <span>Bank</span>
                      <span>
                        {currencySymbol} {(tableOrder.bank || 0).toFixed(2)}
                      </span>
                    </div>
                    {/* Paid and Balance */}
                    <div className="datas">
                      <span>Paid</span>
                      <span>
                        {currencySymbol} {(paid || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="datas">
                      <span>Balance</span>
                      <span>
                        {currencySymbol}
                        {(
                          (paid || 0) -
                          orderDetails.reduce(
                            (sum: number, item: any) =>
                              sum +
                              (Number(item.price) * Number(item.quantity) -
                                parseFloat(item.discount || 0) *
                                  Number(item.quantity)),
                            0,
                          )
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="thank" style={{ marginTop: 12 }}>
                    THANK YOU! COME AGAIN
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 18, display: "flex", gap: 16 }}>
                <button
                  style={{
                    background: "#4763e4",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 24px",
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                  onClick={handleFinalPrintConfirm}
                >
                  Print
                </button>
                <button
                  style={{
                    background: "#ccc",
                    color: "#333",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 24px",
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                  onClick={() => setShowFinalPrintPopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TablePopup;
